import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Timer, CalendarDays, Clock, Users, Briefcase, Plus, UserPlus, 
  Pencil, FileText, AlertCircle, FileDown 
} from 'lucide-react';
import CalendarView from '../../components/CalendarView';

const AnimateurEspaceView = ({
  timeStats,
  activites,
  ateliers,
  users = [],
  currentUserId = null,
  zone = null,
  pendingEmargements,
  onShowActivityReport,
  onShowAddActivite,
  onShowInvitation,
  onEditAtelier,
  onOpenEmargement,
  getActivityTypeLabel
}) => {
  // Vue partagée : retrouve le nom de l'intervenant propriétaire d'une activité
  const getIntervenantName = (intervenantId) => {
    const u = users.find(usr => usr.id === intervenantId);
    return u ? `${u.prenom} ${u.nom}` : null;
  };

  return (
    <div className="space-y-8">
      {/* SECTION 1: Synthèse du Temps */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Timer className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Synthèse du Temps
            </h2>
          </div>
          <Button
            onClick={onShowActivityReport}
            variant="outline"
            size="sm"
            className="rounded-xl"
            data-testid="download-activity-report-btn"
          >
            <FileDown className="w-4 h-4 mr-2" />
            Télécharger rapport
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 rounded-3xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700 font-medium">Mois en cours</p>
                <p className="text-3xl font-bold text-blue-900">{Math.round(timeStats.totalMinutes / 60 * 10) / 10}h</p>
              </div>
              <Timer className="w-10 h-10 text-blue-400" />
            </div>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200 rounded-3xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-700 font-medium">Activités ce mois</p>
                <p className="text-3xl font-bold text-emerald-900">{timeStats.activitiesCount}</p>
              </div>
              <CalendarDays className="w-10 h-10 text-emerald-400" />
            </div>
          </Card>
          <Card className="bg-white border-slate-200 rounded-3xl p-4 shadow-sm">
            <h4 className="font-semibold text-slate-900 mb-2 text-sm">Répartition</h4>
            <div className="space-y-2">
              {Object.entries(timeStats.timeByType).length === 0 ? (
                <p className="text-slate-400 text-xs">Aucune donnée</p>
              ) : (
                Object.entries(timeStats.timeByType).slice(0, 3).map(([type, minutes]) => (
                  <div key={type} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600 truncate">{type}</span>
                    <span className="font-bold text-slate-900">{Math.round(minutes / 60 * 10) / 10}h</span>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </section>

      {/* SECTION 2: Calendrier compact */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays className="w-5 h-5 text-purple-600" />
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Calendrier
          </h2>
        </div>
        <Card className="bg-white border-slate-200 rounded-3xl shadow-sm overflow-hidden">
          <CalendarView userRole="animateur" compact={true} zone={zone} />
        </Card>
      </section>

      {/* SECTION 3: Mon Planning */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Planning des intervenants
            </h2>
          </div>
          <div className="flex gap-2">
            <Button onClick={onShowInvitation} variant="outline" size="sm" className="rounded-xl">
              <UserPlus className="w-4 h-4 mr-2" />
              Inviter RDV
            </Button>
            <Button onClick={onShowAddActivite} size="sm" className="bg-blue-600 hover:bg-blue-700 rounded-xl">
              <Plus className="w-4 h-4 mr-2" />
              Activité
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {activites.length === 0 ? (
            <Card className="col-span-full bg-slate-50 border-slate-200 rounded-3xl p-6 text-center">
              <Briefcase className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">Aucune activité planifiée</p>
            </Card>
          ) : (
            activites.slice(0, 4).map((activite) => {
              const isMine = activite.intervenant_id === currentUserId;
              const ownerName = !isMine ? getIntervenantName(activite.intervenant_id) : null;
              return (
                <Card key={activite.id} className={`rounded-2xl shadow-sm ${isMine ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200'}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium">
                        {getActivityTypeLabel(activite.type)}
                      </span>
                      {isMine ? (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">Moi</span>
                      ) : ownerName && (
                        <span className="text-xs px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full font-medium truncate max-w-[110px]" title={ownerName}>
                          {ownerName}
                        </span>
                      )}
                    </div>
                    <h4 className="font-bold text-slate-900 mt-2 text-sm truncate">{activite.titre}</h4>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" />
                      {activite.date} • {activite.heure_debut}
                    </p>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </section>

      {/* SECTION 4: Ateliers & Émargements */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-emerald-600" />
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Ateliers & Émargements
          </h2>
          {pendingEmargements.length > 0 && (
            <Badge className="bg-red-500 text-white">{pendingEmargements.length} en attente</Badge>
          )}
        </div>

        {/* Émargements en attente */}
        {pendingEmargements.length > 0 && (
          <div className="space-y-3 mb-6">
            <Card className="bg-amber-50 border-amber-200 rounded-2xl p-3 shadow-sm">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertCircle className="w-4 h-4" />
                <p className="text-sm">
                  <strong>{pendingEmargements.length}</strong> événement(s) nécessitent votre attention.
                </p>
              </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pendingEmargements.map((atelier) => (
                <PendingEmargementCard 
                  key={`emarg-${atelier.id}`} 
                  atelier={atelier} 
                  onOpenEmargement={onOpenEmargement} 
                />
              ))}
            </div>
          </div>
        )}

        {/* Prochains ateliers */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ateliers.filter(a => new Date(a.date) >= new Date()).slice(0, 6).map((atelier) => (
            <Card key={atelier.id} className="bg-white border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <Badge className="bg-blue-100 text-blue-700 text-xs">{atelier.zone}</Badge>
                  <span className="text-xs text-slate-500">{atelier.date}</span>
                </div>
                <h4 className="font-bold text-slate-900 mb-1">{atelier.titre}</h4>
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {atelier.heure_debut} - {atelier.heure_fin}
                </p>
                <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                  <Users className="w-3 h-3" />
                  {atelier.participants?.length || 0} participant(s)
                </p>
                <div className="flex gap-2 mt-3">
                  <Button
                    onClick={() => onEditAtelier(atelier)}
                    variant="outline"
                    size="sm"
                    className="flex-1 rounded-xl text-xs"
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Modifier
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
};

// Sous-composant pour les émargements en attente
const PendingEmargementCard = ({ atelier, onOpenEmargement }) => {
  const presentCount = atelier.participants?.filter(p => p.statut_presence === 'present' || p.present).length || 0;
  const signedCount = atelier.participants?.filter(p => p.signature).length || 0;
  const totalParticipants = atelier.participants?.length || 0;
  
  const allStatusDefined = totalParticipants > 0 && 
    atelier.participants.every(p => p.statut_presence && p.statut_presence !== 'non_defini');
  const statusDefinedCount = atelier.participants?.filter(p => 
    p.statut_presence && p.statut_presence !== 'non_defini'
  ).length || 0;
  
  let badgeText, badgeClass, borderClass;
  if (allStatusDefined && atelier.signature_animateur) {
    badgeText = '✓ Émargement complet';
    badgeClass = 'bg-emerald-100 text-emerald-700';
    borderClass = 'border-l-emerald-500';
  } else if (allStatusDefined) {
    badgeText = 'Présences saisies - Signature requise';
    badgeClass = 'bg-blue-100 text-blue-700';
    borderClass = 'border-l-blue-500';
  } else {
    badgeText = `À émarger (${statusDefinedCount}/${totalParticipants})`;
    badgeClass = 'bg-amber-100 text-amber-700';
    borderClass = 'border-l-amber-500';
  }
  
  return (
    <Card className={`bg-white border-l-4 ${borderClass} border-slate-200 rounded-2xl shadow-sm`}>
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-2">
          <div>
            <Badge className={`mb-1 ${badgeClass} text-xs`}>{badgeText}</Badge>
            <h4 className="font-bold text-slate-900">{atelier.titre}</h4>
            <p className="text-xs text-slate-500">{atelier.date} • {atelier.heure_debut}</p>
          </div>
          {atelier.signature_animateur ? (
            <Badge className="bg-emerald-100 text-emerald-700 text-xs">✓ Signé</Badge>
          ) : (
            <Badge className="bg-red-100 text-red-700 text-xs">Non signé</Badge>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-600 mb-3">
          <span>{presentCount} présent(s)</span>
          <span>{signedCount} signature(s)</span>
        </div>
        <Button
          onClick={() => onOpenEmargement(atelier)}
          size="sm"
          className={`w-full rounded-xl text-xs ${allStatusDefined && !atelier.signature_animateur ? 'bg-blue-600 hover:bg-blue-700' : atelier.signature_animateur ? 'bg-slate-600 hover:bg-slate-700' : 'bg-amber-600 hover:bg-amber-700'}`}
        >
          <FileText className="w-3 h-3 mr-1" />
          {atelier.signature_animateur ? 'Voir / Modifier' : allStatusDefined ? 'Signer' : 'Saisir présences'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default AnimateurEspaceView;
